"use client";

import { ArrowRight, ChartBar as BarChart3, Settings, Search, ChevronRight, ChartBar as BarChart, Zap, Layers, Cpu } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Services() {
  const header = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const cards = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const plans = [{
    icon: Search,
    title: "OTA Revenue Management",
    bestFor: "Best for maximizing visibility & conversion on OTAs",
    features: ["Strategic OTA listing optimization", "Dynamic rate parity management", "Competitor benchmarking & analysis", "Review response & reputation strategy", "Data-driven inventory allocation"],
    price: "Custom",
    popular: false
  }, {
    icon: Settings,
    title: "Hospitality Tech Stack",
    bestFor: "Best for seamless tech integration & automation",
    features: ["PMS & Channel Manager selection", "Revenue management system setup", "Booking engine conversion audit", "Tech stack performance monitoring", "Automated reporting workflows"],
    price: "Custom",
    popular: true
  }, {
    icon: BarChart3,
    title: "Revenue Audits",
    bestFor: "Best for identifying hidden revenue opportunities",
    features: ["Comprehensive revenue performance audit", "Pricing strategy gap analysis", "Distribution channel efficiency review", "Market demand forecasting insights", "Actionable growth roadmap delivery"],
    price: "Custom",
    popular: false
  }];
  const sidebarLinks = [{
    title: "Hotel Revenue Management Consulting",
    href: "/HotelRevenueManagementConsultingPage",
    icon: BarChart
  }, {
    title: "Dynamic Pricing & Forecasting",
    href: "/DynamicPricingForecastingServices2Page",
    icon: Zap
  }, {
    title: "Channel Manager Setup",
    href: "/ChannelManagerSetupDistributionStrategyPage",
    icon: Layers
  }, {
    title: "Tool Expertise & Tech Stack",
    href: "/ToolExpertiseTechnologyStackPage",
    icon: Cpu
  }];
  return <section data-section-id="774" id="servicenavigation" className="relative bg-background text-foreground overflow-hidden py-20 md:py-28 lg:py-32">
      <div aria-hidden="true" className="pointer-events-none absolute -top-32 -right-32 w-[500px] h-[500px] rounded-lg opacity-30 blur-3xl" style={{
      background: "radial-gradient(circle, var(--primary) 0%, transparent 70%)"
    }} />

      <div className="relative mx-auto max-w-7xl px-6 lg:px-8">
        <div ref={header.ref} className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-end mb-16 lg:mb-20 transition-all duration-700 ${header.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="lg:col-span-7">
            <p className="font-default text-xs uppercase tracking-[0.2em] mb-5" style={{
            color: "var(--secondary)"
          }}>
              Our Expertise
            </p>
            <h2 className="font-default text-4xl md:text-5xl lg:text-6xl font-bold text-foreground leading-[1.05] tracking-tight">
              Strategic Revenue
              <br />
              Solutions for Hotels.
            </h2>
          </div>
          <div className="lg:col-span-5 lg:pb-3">
            <p className="font-default text-base md:text-lg text-muted-foreground leading-relaxed mb-5 max-w-md">
              Unlock your property's full potential with data-driven strategies, 
              modern tech implementation, and expert revenue management tailored 
              to your unique market position.
            </p>
            <Link to="/OurServicesPage" className="inline-flex items-center gap-2 font-medium text-foreground hover:gap-3 transition-all group">
              <span className="border-b border-foreground/30 pb-0.5">
                View all services
              </span>
              <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {}
          <aside className="lg:col-span-3 hidden lg:block">
            <div className="sticky top-24 space-y-8">
              <div>
                <h3 className="font-default text-sm uppercase tracking-widest text-foreground/50 mb-6">
                  Service Navigation
                </h3>
                <nav className="flex flex-col gap-2">
                  {sidebarLinks.map((link, i) => <Link key={i} data-index={i} to={link.href} className="group flex items-center gap-3 p-3 rounded-lg transition-all hover:bg-accent hover:text-accent-foreground border border-transparent hover:border-border">
                      <link.icon className="size-4 text-secondary" />
                      <span className="text-sm font-medium">{link.title}</span>
                      <ChevronRight className="size-3 ml-auto opacity-0 -translate-x-2 transition-all group-hover:opacity-100 group-hover:translate-x-0" />
                    </Link>)}
                </nav>
              </div>
              
              <div className="p-6 rounded-lg bg-primary text-primary-foreground">
                <h4 className="font-bold mb-2">Need a Custom Audit?</h4>
                <p className="text-xs text-primary-foreground/70 mb-4">Get a comprehensive review of your current revenue strategy.</p>
                <Button asChild variant="secondary" size="sm" className="w-full rounded-lg">
                  <Link to="/ContactPage">Request Audit</Link>
                </Button>
              </div>
            </div>
          </aside>

          {}
          <div ref={cards.ref} className="lg:col-span-9 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 items-stretch">
            {plans.map((plan, i) => {
            const Icon = plan.icon;
            return <div key={i} data-index={i} className={`group relative flex flex-col rounded-lg bg-card border border-border p-8 lg:p-10 transition-all duration-500 hover:-translate-y-1 ${plan.popular ? "lg:-translate-y-2 shadow-lg" : "shadow"} ${cards.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
              transitionDelay: cards.isVisible ? `${i * 120}ms` : "0ms"
            }}>
                  {plan.popular && <Badge className="absolute top-6 right-6 font-default text-[10px] uppercase tracking-widest px-3 py-1 border-0" style={{
                backgroundColor: "var(--secondary)",
                color: "var(--primary-foreground)"
              }}>
                      Recommended
                    </Badge>}

                  <div className="flex items-center justify-center size-16 rounded-lg mb-7 transition-transform duration-500 group-hover:scale-105" style={{
                backgroundColor: "hsla(43 46% 55% / 0.1)"
              }}>
                    <Icon className="size-7" style={{
                  color: "var(--secondary)"
                }} strokeWidth={1.5} />
                  </div>

                  <h3 className="font-default text-2xl md:text-3xl font-bold text-foreground mb-3 tracking-tight">
                    {plan.title}
                  </h3>

                  <p className="font-default text-sm leading-relaxed mb-6 text-muted-foreground">
                    {plan.bestFor}
                  </p>

                  <ul className="flex flex-col gap-3 mb-8 flex-1">
                    {plan.features.map((feature, fi) => <li key={fi} data-index={fi} className="flex items-start gap-3 text-sm text-foreground/80">
                        <span className="flex items-center justify-center size-5 rounded-md mt-0.5 shrink-0" style={{
                    backgroundColor: "hsla(43 46% 55% / 0.15)"
                  }}>
                          <Check className="size-3" style={{
                      color: "var(--secondary)"
                    }} strokeWidth={3} />
                        </span>
                        <span className="font-default leading-relaxed">{feature}</span>
                      </li>)}
                  </ul>

                  <div className="border-t border-border pt-6 mt-auto">
                    <div className="flex items-baseline gap-2 mb-5">
                      <span className="font-default text-xs uppercase tracking-wider text-muted-foreground">
                        Pricing
                      </span>
                      <span className="font-default text-4xl font-bold text-foreground">
                        {plan.price}
                      </span>
                    </div>
                    <Button asChild variant="ghost" className="h-auto p-0 font-medium text-foreground hover:bg-transparent hover:text-foreground group/btn">
                      <Link to="/ContactPage" className="inline-flex items-center gap-2">
                        <span className="border-b border-foreground/40 pb-0.5">
                          Inquire now
                        </span>
                        <ArrowRight className="size-4 transition-transform group-hover/btn:translate-x-1" />
                      </Link>
                    </Button>
                  </div>
                </div>;
          })}
          </div>
        </div>

        {}
        <div className="mt-24 pt-16 border-t border-border">
          <div className="max-w-3xl">
            <h3 className="font-default text-2xl font-bold text-foreground mb-8 tracking-tight">
              Explore More Revenue Solutions
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {sidebarLinks.map((link, i) => <Link key={i} data-index={i} to={link.href} className="group flex items-center justify-between p-5 rounded-lg bg-card border border-border hover:border-secondary/50 transition-all">
                  <div className="flex items-center gap-4">
                    <div className="size-10 rounded-lg bg-accent flex items-center justify-center">
                      <link.icon className="size-5 text-secondary" />
                    </div>
                    <span className="font-medium text-foreground group-hover:text-secondary transition-colors">
                      {link.title}
                    </span>
                  </div>
                  <ArrowRight className="size-4 opacity-0 group-hover:opacity-100 transition-all -translate-x-2 group-hover:translate-x-0" />
                </Link>)}
            </div>
          </div>
        </div>
      </div>
    </section>;
}
function Check({
  className,
  style,
  strokeWidth
}: {
  className?: string;
  style?: any;
  strokeWidth?: number;
}) {
  return <svg className={className} style={style} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12"></polyline>
    </svg>;
}